var _Editor_container, _Editor_clock, _Editor_plugins, _Editor_configureList, _Editor_ctx, _Editor_pre, _Editor_loadInternal;
import { __awaiter, __classPrivateFieldGet } from "tslib";
/* Copyright 2021, Milkdown by Mirone. */
import { createContainer } from '../context';
import { commands, config, editorState, editorView, init, inputRules, keymap, nodeView, parser, schema, serializer, } from '../internal-plugin';
import { createClock } from '../timing';
import { Ctx } from './ctx';
import { Pre } from './pre';
/**
 * Get the milkdown editor constructor
 */
export class Editor {
    constructor() {
        _Editor_container.set(this, createContainer());
        _Editor_clock.set(this, createClock());
        _Editor_plugins.set(this, new Set());
        _Editor_configureList.set(this, []);
        _Editor_ctx.set(this, new Ctx(__classPrivateFieldGet(this, _Editor_container, "f"), __classPrivateFieldGet(this, _Editor_clock, "f")));
        _Editor_pre.set(this, new Pre(__classPrivateFieldGet(this, _Editor_container, "f"), __classPrivateFieldGet(this, _Editor_clock, "f")));
        _Editor_loadInternal.set(this, () => {
            const internalPlugins = [
                schema,
                parser,
                serializer,
                nodeView,
                commands,
                keymap,
                inputRules,
                editorState,
                editorView,
            ];
            const configPlugin = config((x) => __awaiter(this, void 0, void 0, function* () {
                yield Promise.all(__classPrivateFieldGet(this, _Editor_configureList, "f").map((fn) => fn(x)));
            }));
            this.use(internalPlugins.concat(init(this)).concat(configPlugin));
        });
        /**
         * Use one plugin or a list of plugins for current editor.
         *
         * @example
         * ```
         * Editor.make()
         *   .use(plugin)
         *   .use([pluginA, pluginB])
         * ```
         *
         * @param plugins - A list of plugins, or one plugin.
         * @returns Editor instance.
         */
        this.use = (plugins) => {
            [plugins].flat().forEach((plugin) => {
                __classPrivateFieldGet(this, _Editor_plugins, "f").add(plugin(__classPrivateFieldGet(this, _Editor_pre, "f")));
            });
            return this;
        };
        /**
         * Config the context for current editor.
         *
         * @param configure - The function that configure current editor, can be async, with context as parameter.
         * @returns Editor instance.
         */
        this.config = (configure) => {
            __classPrivateFieldGet(this, _Editor_configureList, "f").push(configure);
            return this;
        };
        /**
         * Create the editor UI.
         *
         * @example
         * ```
         * Editor.make().use(nord).use(commonmark).create()
         * ```
         *
         * @returns A promise object, will be resolved as editor instance after create finish.
         */
        this.create = () => __awaiter(this, void 0, void 0, function* () {
            __classPrivateFieldGet(this, _Editor_loadInternal, "f").call(this);
            yield Promise.all([...__classPrivateFieldGet(this, _Editor_plugins, "f")].map((loader) => {
                return loader(__classPrivateFieldGet(this, _Editor_ctx, "f"));
            }));
            return this;
        });
        /**
         * Get the context value in a running editor on demand and return the action result.
         *
         * @example
         * ```
         * import { Editor, editorViewCtx, serializerCtx } from '@milkdown/core';
         * async function playWithEditor() {
         *     const editor = await Editor.make().use(commonmark).create();
         *
         *     const getMarkdown = () =>
         *         editor.action((ctx) => {
         *             const editorView = ctx.get(editorViewCtx);
         *             const serializer = ctx.get(serializerCtx);
         *             return serializer(editorView.state.doc);
         *         });
         *
         *     // get markdown string:
         *     getMarkdown();
         * }
         * ```
         *
         * @param action - The function that get editor context and return the action result.
         * @returns The action result.
         */
        this.action = (action) => action(__classPrivateFieldGet(this, _Editor_ctx, "f"));
    }
    /**
     * Create a new editor instance.
     *
     * @returns The new editor instance been created.
     */
    static make() {
        return new Editor();
    }
}
_Editor_container = new WeakMap(), _Editor_clock = new WeakMap(), _Editor_plugins = new WeakMap(), _Editor_configureList = new WeakMap(), _Editor_ctx = new WeakMap(), _Editor_pre = new WeakMap(), _Editor_loadInternal = new WeakMap();
//# sourceMappingURL=editor.js.map